import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Users, Baby, GraduationCap, MoreHorizontal,
  Utensils, Settings, LogOut, Bug, Coins, Building2, Calendar
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useTheme } from '@/lib/ThemeContext';
import { useEnabledModulesForUser } from '@/hooks/useBranchModules';

// Main bottom nav items - Grafik between Struktura and Rozliczenia (filtered by module toggles)
const NAV_ITEMS_BASE = [
  { name: 'Start', icon: Home, page: '/Home', tutorialId: 'nav-home' },
  { name: 'Struktura', icon: Building2, action: 'struktura', tutorialId: 'nav-struktura' },
  { name: 'Grafik', icon: Calendar, page: '/StaffSchedule', tutorialId: 'nav-grafik', module: 'grafik' },
  { name: 'Jadlospis', icon: Utensils, page: '/MenuManagement', tutorialId: 'nav-menu', module: 'jadlospis' },
  { name: 'Rozliczenia', icon: Coins, page: '/Billing', tutorialId: 'nav-billing' },
];

// Structure sub-items (people & organization management) - Grafik is a top-level nav item
const STRUKTURA_ITEMS_BASE = [
  { name: 'Grupy', icon: Users, page: '/Groups', description: 'Zarzadzaj grupami dzieci' },
  { name: 'Dzieci', icon: Baby, page: '/Children', description: 'Lista dzieci w placowce' },
  { name: 'Nauczyciele', icon: GraduationCap, page: '/Teachers', description: 'Kadra pedagogiczna' },
  { name: 'Rodzice', icon: Users, page: '/Parents', description: 'Rodzice i opiekunowie' },
];

// More menu items
const MORE_ITEMS = [
  { name: 'Ustawienia', icon: Settings, page: '/BranchSettings' },
];

export default function BranchAdminBottomNav({ onLogout, onReportBug }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [strukturaOpen, setStrukturaOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const { getActiveColors } = useTheme();
  const themeColors = getActiveColors();
  const { isModuleEnabled } = useEnabledModulesForUser();

  const navItems = useMemo(() =>
    NAV_ITEMS_BASE.filter((item) => !item.module || isModuleEnabled(item.module)),
    [isModuleEnabled]
  );
  const strukturaItems = useMemo(() =>
    STRUKTURA_ITEMS_BASE.filter((item) => !item.module || isModuleEnabled(item.module)),
    [isModuleEnabled]
  );

  const isActive = (page) => {
    return location.pathname === page || location.pathname === page.toLowerCase();
  };

  // Check if any struktura page is active
  const isStrukturaActive = strukturaItems.some((item) => isActive(item.page));

  const handleNavClick = (item) => {
    if (item.action === 'struktura') {
      setStrukturaOpen(true);
    } else if (item.page) {
      navigate(item.page);
    }
  };

  const handleStrukturaItemClick = (page) => {
    setStrukturaOpen(false);
    navigate(page);
  };

  const handleMoreItemClick = (page) => {
    setMoreOpen(false);
    navigate(page);
  };

  return (
    <>
      {/* Bottom Navigation Bar - Uses theme colors */}
      <nav className={cn("fixed bottom-0 left-0 right-0 z-50 safe-area-bottom", themeColors.headerBg)}>
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = item.action === 'struktura' ? isStrukturaActive : isActive(item.page);
            return (
              <button
                key={item.name}
                onClick={() => handleNavClick(item)}
                data-tutorial={item.tutorialId}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors",
                  active ? "text-white" : "text-white/70 hover:text-white"
                )}
              >
                <Icon className="w-5 h-5" />
                <span className={cn(
                  "text-xs",
                  active ? "font-semibold" : "font-medium"
                )}>
                  {item.name}
                </span>
              </button>
            );
          })}

          {/* More Button */}
          <button
            onClick={() => setMoreOpen(true)}
            className={cn(
              "flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors",
              moreOpen ? "text-white" : "text-white/70 hover:text-white"
            )}
          >
            <MoreHorizontal className="w-5 h-5" />
            <span className="text-xs font-medium">Inne</span>
          </button>
        </div>
      </nav>

      {/* Struktura Sheet (Bottom Drawer) */}
      <Sheet open={strukturaOpen} onOpenChange={setStrukturaOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="pb-2">
            <SheetTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Struktura placowki
            </SheetTitle>
          </SheetHeader>

          <div className="grid grid-cols-2 gap-3 pb-8 pt-2">
            {strukturaItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.page);
              return (
                <button
                  key={item.page}
                  onClick={() => handleStrukturaItemClick(item.page)}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 rounded-xl transition-all",
                    active
                      ? "bg-indigo-100 text-indigo-700 ring-2 ring-indigo-500"
                      : "bg-slate-50 hover:bg-slate-100 text-slate-700"
                  )}
                >
                  <div className={cn(
                    "p-3 rounded-full",
                    active ? "bg-indigo-200" : "bg-white shadow-sm"
                  )}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="text-center">
                    <span className={cn(
                      "block text-sm",
                      active ? "font-semibold" : "font-medium"
                    )}>
                      {item.name}
                    </span>
                    <span className="text-xs text-slate-500 mt-0.5 block">
                      {item.description}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* More Sheet (Bottom Drawer) */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader className="pb-4">
            <SheetTitle>Inne opcje</SheetTitle>
          </SheetHeader>

          <div className="space-y-2 pb-8">
            {MORE_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.page);
              return (
                <button
                  key={item.page}
                  onClick={() => handleMoreItemClick(item.page)}
                  className={cn(
                    "flex items-center gap-4 w-full p-4 rounded-xl transition-colors",
                    active
                      ? "bg-indigo-50 text-indigo-600"
                      : "hover:bg-slate-50 text-slate-700"
                  )}
                >
                  <div className={cn(
                    "p-2 rounded-lg",
                    active ? "bg-indigo-100" : "bg-slate-100"
                  )}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className={cn(
                    "font-medium",
                    active && "font-semibold"
                  )}>
                    {item.name}
                  </span>
                </button>
              );
            })}

            {/* Divider */}
            <div className="border-t border-slate-200 my-4" />

            {/* Report Bug Button */}
            <button
              onClick={() => {
                setMoreOpen(false);
                onReportBug?.();
              }}
              className="flex items-center gap-4 w-full p-4 rounded-xl transition-colors hover:bg-amber-50 text-amber-700"
            >
              <div className="p-2 rounded-lg bg-amber-100">
                <Bug className="w-5 h-5" />
              </div>
              <span className="font-medium">Zglos problem</span>
            </button>

            {/* Logout Button */}
            <button
              onClick={() => {
                setMoreOpen(false);
                onLogout?.();
              }}
              className="flex items-center gap-4 w-full p-4 rounded-xl transition-colors hover:bg-red-50 text-red-600"
            >
              <div className="p-2 rounded-lg bg-red-100">
                <LogOut className="w-5 h-5" />
              </div>
              <span className="font-medium">Wyloguj</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
